/**
 * GoToDatePopup.tsx
 *
 * Modal overlay that lets the user jump to any date in the calendar by
 * scrolling through a vertically stacked list of month grids.
 *
 * ## Layout
 * - **Header** — title ("Go to Date") and × close button.
 * - **Scrollable body** — 49 month grids (24 months back, current month, 24 months forward).
 *   The list is automatically scrolled to the current month on mount.
 * - **Footer** — displays the selected date as text, plus Cancel and OK buttons.
 *   The OK button is disabled until a day is selected.
 *
 * ## Interaction
 * - Tapping a day cell selects it (highlighted in primary gold).
 * - Today's date is highlighted with a subtle gold ring.
 * - Clicking the backdrop, pressing Escape, or clicking Cancel calls `onClose`.
 * - Clicking OK calls `onConfirm(selectedDate)`.
 *
 * ## Props
 * @prop onConfirm - Called with the chosen `Date` when the user clicks OK.
 * @prop onClose   - Called when the popup should be dismissed without a selection.
 */

'use client';

import React, { useRef, useEffect, useState, useCallback } from 'react';

/** Abbreviated day-of-week headers for the grid (Monday-first). */
const DAY_HEADERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

/** Full month names used in the month header and footer date display. */
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

/** Props accepted by `GoToDatePopup`. */
interface Props {
  /** Called with the selected date when the user confirms their choice. */
  onConfirm: (date: Date) => void;
  /** Called when the popup should close without a selection. */
  onClose: () => void;
}

/**
 * Return the number of days in a given month.
 *
 * @param year  - Full four-digit year.
 * @param month - Zero-based month index (0 = January).
 * @returns Number of days in the specified month.
 */
function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

/**
 * Return the zero-based weekday index of the first day of a month,
 * adjusted so that Monday = 0 (ISO week convention).
 *
 * @param year  - Full four-digit year.
 * @param month - Zero-based month index.
 * @returns 0 for Monday … 6 for Sunday.
 */
function getFirstDayOfMonth(year: number, month: number): number {
  let d = new Date(year, month, 1).getDay();
  return d === 0 ? 6 : d - 1;
}

/** Props accepted by the internal `MonthGrid` sub-component. */
interface MonthGridProps {
  /** Full four-digit year for this grid. */
  year: number;
  /** Zero-based month index for this grid. */
  month: number;
  /** Currently selected date, or `null` if none. */
  selectedDate: Date | null;
  /** Today's date (used to highlight the current day). */
  today: Date;
  /** Called when the user taps a day cell. */
  onSelectDay: (date: Date) => void;
}

function MonthGrid({ year, month, selectedDate, today, onSelectDay }: MonthGridProps) {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDayOffset = getFirstDayOfMonth(year, month);

  // Build cells: empty slots for offset, then current-month days only
  const cells: { day: number | null; dateObj: Date | null }[] = [];

  for (let i = 0; i < firstDayOffset; i++) {
    cells.push({ day: null, dateObj: null });
  }

  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, dateObj: new Date(year, month, d) });
  }

  // Pad to complete the last row (multiple of 7), but do NOT add a full extra row
  const remainder = cells.length % 7;
  if (remainder !== 0) {
    for (let i = 0; i < 7 - remainder; i++) {
      cells.push({ day: null, dateObj: null });
    }
  }

  const todayStr = today.toDateString();
  const selectedStr = selectedDate ? selectedDate.toDateString() : null;

  return (
    <div className="mb-4">
      {/* Month header */}
      <div className="flex items-center justify-center gap-2 mb-2">
        <span className="text-primary/40 text-xs" aria-hidden>✦</span>
        <h3 className="font-display text-sm text-primary tracking-widest uppercase font-semibold">
          {MONTH_NAMES[month]}
        </h3>
        <span className="font-display text-xs text-muted-foreground tracking-wider">{year}</span>
        <span className="text-primary/40 text-xs" aria-hidden>✦</span>
      </div>
      <div className="ornament-line opacity-30 mb-2" />

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-0 mb-1">
        {DAY_HEADERS.map((h, i) => (
          <div
            key={`hdr-${month}-${i}`}
            className="text-center font-display text-[10px] text-muted-foreground/60 tracking-wider uppercase py-0.5 font-bold"
          >
            {h}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 gap-0">
        {cells.map((cell, idx) => {
          if (!cell.day || !cell.dateObj) {
            return <div key={`cell-${month}-${idx}`} className="aspect-square" />;
          }
          const cellStr = cell.dateObj.toDateString();
          const isToday = cellStr === todayStr;
          const isSelected = cellStr === selectedStr;

          return (
            <button
              key={`cell-${month}-${idx}`}
              onClick={() => onSelectDay(cell.dateObj!)}
              className={`
                aspect-square flex items-center justify-center rounded-full text-xs font-display transition-all duration-150
                ${isSelected
                  ? 'bg-primary text-background font-semibold shadow-[0_0_8px_rgba(201,168,76,0.4)]'
                  : isToday
                  ? 'bg-primary/20 text-primary border border-primary/40' :'text-foreground/80 hover:bg-primary/10 hover:text-primary cursor-pointer'
                }
              `}
            >
              {cell.day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function GoToDatePopup({ onConfirm, onClose }: Props) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Build list of months: 24 months back to 24 months forward
  const MONTHS_BACK = 24;
  const MONTHS_FORWARD = 24;
  const totalMonths = MONTHS_BACK + 1 + MONTHS_FORWARD;

  const months = Array.from({ length: totalMonths }, (_, i) => {
    const offset = i - MONTHS_BACK;
    let d = new Date(today.getFullYear(), today.getMonth() + offset, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  // Scroll to current month on mount
  useEffect(() => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const currentIndex = MONTHS_BACK;
      const itemHeight = container.scrollHeight / totalMonths;
      container.scrollTop = currentIndex * itemHeight;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectDay = useCallback((date: Date) => {
    setSelectedDate(date);
  }, []);

  const handleOk = () => {
    if (selectedDate) {
      onConfirm(selectedDate);
    }
  };

  // ESC key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onPointerMove={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-label="Go to date">

      <div className="relative w-full max-w-sm mx-4 bg-card border border-border/60 rounded-2xl shadow-2xl overflow-hidden animate-modal-in flex flex-col"
        style={{ maxHeight: '85vh' }}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border/40 flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-primary text-sm animate-gold-pulse select-none" aria-hidden>✦</span>
            <h2 className="font-decorative text-sm text-primary tracking-widest uppercase">Go to Date</h2>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-primary transition-colors duration-200 text-xl leading-none p-1 rounded"
            aria-label="Close">
            ×
          </button>
        </div>

        {/* Scrollable months */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto scrollbar-arcane px-5 py-4"
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onPointerMove={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}>
          {months.map(({ year, month }) => (
            <MonthGrid
              key={`goto-${year}-${month}`}
              year={year}
              month={month}
              selectedDate={selectedDate}
              today={today}
              onSelectDay={handleSelectDay}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-5 py-3 border-t border-border/40 flex items-center justify-between gap-3">
          <span className="font-body text-xs text-muted-foreground italic">
            {selectedDate
              ? `${selectedDate.getDate()} ${MONTH_NAMES[selectedDate.getMonth()]} ${selectedDate.getFullYear()}`
              : 'Select a day'}
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-1.5 rounded-lg border border-border/40 text-xs font-display tracking-wider text-muted-foreground hover:text-foreground hover:border-border/70 transition-all duration-150">
              Cancel
            </button>
            <button
              onClick={handleOk}
              disabled={!selectedDate}
              className={`px-4 py-1.5 rounded-lg border text-xs font-display tracking-wider transition-all duration-150
                ${selectedDate
                  ? 'border-primary/60 bg-primary/10 text-primary hover:bg-primary/20' :'border-border/20 text-muted-foreground/30 cursor-not-allowed'}`}>
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
